import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Offcanvas } from "bootstrap";
import moment from "moment";
import { buildPresetRanges } from "../../../Layout/Common/customDateRangePicker";
import { CustomSingleDatePicker } from "../../../Layout/Common/customSingleDatePicker";

const VALUE_FORMAT = "YYYY-MM-DD";
const DISPLAY_FORMAT = "DD/MM/YYYY";
const CUSTOM_VALUE = "custom";
const PRESET_LABELS = Object.keys(buildPresetRanges());

const toValue = (value) => {
  const parsed = moment(value, [VALUE_FORMAT, DISPLAY_FORMAT], true);
  return parsed.isValid() ? parsed.format(VALUE_FORMAT) : "";
};

const today = () => moment().format(VALUE_FORMAT);

export const OrderFilterMobile = forwardRef(({ searchValue, range, onApply, onReset }, ref) => {
  const canvasRef = useRef(null);
  const instanceRef = useRef(null);
  const [draftSearch, setDraftSearch] = useState("");
  const [preset, setPreset] = useState(CUSTOM_VALUE);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    if (!canvasRef.current) return undefined;
    instanceRef.current = new Offcanvas(canvasRef.current, { backdrop: true });
    return () => {
      instanceRef.current?.dispose();
      instanceRef.current = null;
    };
  }, []);

  useImperativeHandle(ref, () => ({
    show: () => {
      setDraftSearch(searchValue || "");
      setPreset(CUSTOM_VALUE);
      setFromDate(range ? toValue(range.startDate) : "");
      setToDate(range ? toValue(range.endDate) : "");
      instanceRef.current?.show();
    },
  }));

  const isInvoiceSearch = String(draftSearch || "").trim().length > 0;

  const handlePresetChange = (label) => {
    setPreset(label);
    if (label === CUSTOM_VALUE) return;

    const [start, end] = buildPresetRanges()[label];
    setFromDate(start.format(VALUE_FORMAT));
    setToDate(end.format(VALUE_FORMAT));
  };

  const handleFromChange = (value) => {
    setPreset(CUSTOM_VALUE);
    setFromDate(value);
    if (toDate && value > toDate) setToDate(value);
  };

  const handleToChange = (value) => {
    setPreset(CUSTOM_VALUE);
    setToDate(value);
    if (fromDate && value < fromDate) setFromDate(value);
  };

  const handleApply = () => {
    const dtRange = fromDate && toDate ? { startDate: fromDate, endDate: toDate } : null;
    onApply(dtRange, draftSearch);
    instanceRef.current?.hide();
  };

  const handleResetClick = () => {
    setDraftSearch("");
    setPreset(CUSTOM_VALUE);
    setFromDate("");
    setToDate("");
    onReset();
    instanceRef.current?.hide();
  };

  return (
    <div className="offcanvas offcanvas-end" tabIndex={-1} id="orderFilterCanvas" ref={canvasRef}>
      <div className="offcanvas-header">
        <i className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close" />
        <h5>Filters</h5>
      </div>
      <div className="offcanvas-body offcanvas-filterBody">
        <div className="orderFilter-field">
          <label htmlFor="orderFilterSearch">Order No or Invoice No</label>
          <div className="input-group orders-search">
            <i className="fa-solid fa-magnifying-glass" />
            <input
              type="text"
              id="orderFilterSearch"
              className="form-control"
              placeholder="Order No or Invoice No"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
            />
          </div>
        </div>

        <div className={`orderFilter-dates${isInvoiceSearch ? " is-disabled" : ""}`}>
          <div className="orderFilter-field">
            <label htmlFor="orderFilterPreset">Date Range</label>
            <select
              id="orderFilterPreset"
              className="form-control orderFilter-select"
              value={preset}
              disabled={isInvoiceSearch}
              onChange={(e) => handlePresetChange(e.target.value)}
            >
              {PRESET_LABELS.map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
              <option value={CUSTOM_VALUE}>Custom</option>
            </select>
          </div>

          <div className="orderFilter-field">
            <label>From Date</label>
            <CustomSingleDatePicker
              value={fromDate}
              onChange={handleFromChange}
              maxDate={toDate || today()}
              placeholder="From Date"
              disabled={isInvoiceSearch}
            />
          </div>

          <div className="orderFilter-field">
            <label>To Date</label>
            <CustomSingleDatePicker
              value={toDate}
              onChange={handleToChange}
              minDate={fromDate}
              maxDate={today()}
              placeholder="To Date"
              disabled={isInvoiceSearch}
            />
          </div>

          {isInvoiceSearch && (
            <p className="orders-daterange-note">
              <i className="fa-solid fa-circle-info" />
              Date filter is not applied when searching by Order/Invoice No.
            </p>
          )}
        </div>
      </div>
      <div className="offcanvas-footer offcanvas-filterFooter">
        <button type="button" className="btn btn-unavailable fs-13 fw-medium mb-0" onClick={handleResetClick}>
          Reset
        </button>
        <div className="button-effect">
          <button type="button" className="btn btn-addcart fs-13 fw-medium mb-0" onClick={handleApply}>
            Search
          </button>
        </div>
      </div>
    </div>
  );
});
